import { useDebounceCallback } from "usehooks-ts";
import { classNames } from "@via/utils/helpers";
import { useEffect, useState } from "react";

type FilterItem = { id: string; value: unknown };
export enum PremiumDatasetViewType {
  My = "my",
  Shared = "shared",
  Public = "public",
}

type DataCollectionFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  showOwnerNameFilter?: boolean;
  showPremiumDatasetViewButtons?: boolean;
  premiumDatasetViewType?: PremiumDatasetViewType;
  onPremiumDatasetViewTypeChange?: (type: PremiumDatasetViewType) => void;
};

const DataCollectionFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
  showOwnerNameFilter = false,
  showPremiumDatasetViewButtons = false,
  premiumDatasetViewType,
  onPremiumDatasetViewTypeChange,
}: DataCollectionFilterProps) => {
  const selectedFilterClass =
    "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md";
  const unSelectedFilterClass = "bg-white";
  const leftButtonClass = "rounded-l-md min-w-[110px]";
  const middleButtonClass = "-ml-px min-w-[150px]";
  const rightButtonClass = "rounded-r-md -ml-px min-w-[110px]";
  const defaultButtonClass =
    "relative inline-flex flex-none justify-center h-9 items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap";

  const [selectedViewType, setSelectedViewType] =
    useState<PremiumDatasetViewType>(
      premiumDatasetViewType ?? PremiumDatasetViewType.My
    );

  const nameFilterDefault =
    (columnFilters.find(filter => filter.id === "name")?.value as string) || "";

  const ownerNameFilterDefault =
    (columnFilters.find(filter => filter.id === "owner.name")
      ?.value as string) || "";

  const debounceInput = useDebounceCallback((value, id) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  // Keep local selected state in sync with parent-provided value (when controlled)
  useEffect(() => {
    if (!showPremiumDatasetViewButtons) return;
    if (!premiumDatasetViewType) return;
    setSelectedViewType(premiumDatasetViewType);
  }, [showPremiumDatasetViewButtons, premiumDatasetViewType]);

  const handleSelectViewType = (type: PremiumDatasetViewType) => {
    setSelectedViewType(type);
    onPremiumDatasetViewTypeChange?.(type);
    setPage(0);
    setPageSize(10);
  };

  return (
    <>
      <div className="w-full my-2 flex flex-col items-center justify-between gap-2 md:flex-row">
        <div className="w-full flex-1">
          <input
            type="text"
            placeholder="Filter By Name"
            className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            defaultValue={nameFilterDefault}
            onChange={e => debounceInput(e.target.value, "name")}
          />
        </div>
        {showOwnerNameFilter && (
          <div className="w-full flex-1">
            <div className="w-full flex flex-col gap-2 md:flex-row md:items-center">
              <input
                type="text"
                placeholder="Filter By Owner Name"
                className="w-full md:w-auto flex-1 min-w-0 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                defaultValue={ownerNameFilterDefault}
                onChange={e => debounceInput(e.target.value, "owner.name")}
              />
              {showPremiumDatasetViewButtons && (
                <span className="w-full isolate inline-flex rounded-md shadow-sm md:w-auto shrink-0 md:flex-none">
                  <button
                    type="button"
                    onClick={() =>
                      handleSelectViewType(PremiumDatasetViewType.My)
                    }
                    className={classNames(
                      defaultButtonClass,
                      leftButtonClass,
                      selectedViewType === PremiumDatasetViewType.My
                        ? selectedFilterClass
                        : unSelectedFilterClass
                    )}>
                    My Datasets
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleSelectViewType(PremiumDatasetViewType.Shared)
                    }
                    className={classNames(
                      defaultButtonClass,
                      middleButtonClass,
                      selectedViewType === PremiumDatasetViewType.Shared
                        ? selectedFilterClass
                        : unSelectedFilterClass
                    )}>
                    Shared With Me
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleSelectViewType(PremiumDatasetViewType.Public)
                    }
                    className={classNames(
                      defaultButtonClass,
                      rightButtonClass,
                      selectedViewType === PremiumDatasetViewType.Public
                        ? selectedFilterClass
                        : unSelectedFilterClass
                    )}>
                    Public
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DataCollectionFilter;
